import React, { useCallback, useContext, useMemo } from 'react'

import { ChatSendMessageToastContext } from '@/components/chat/ChatSendMessageToast/ChatSendMessageToastProvider'
import {
  getUserByUsernameWithTokenAndUrl,
  mixPanelTrack,
} from '@/services/user'
import { ProfilePageActionTypes, ProfilePageState } from 'interfaces'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { IUser, getUserShareTitle } from '../../interfaces/user'
import { UserContext } from '../user/UserContext'
import { ProfilePageContext } from './ProfilePageContext'
import { profilePageReducer } from './profilePageReducer'

interface Props {
  children: React.ReactNode
}

const ProfilePage_INITAL_SATATE: ProfilePageState = {}

export const ProfileProvider: React.FC<Props> = ({ children }) => {
  const { user } = useContext(UserContext)
  const { showToast } = useContext(ChatSendMessageToastContext)
  const [currentPath, setCurrentPath] = React.useState<string>('')
  const router = useRouter()
  const { query, pathname } = router
  const [state, dispatch] = React.useReducer(
    profilePageReducer,
    ProfilePage_INITAL_SATATE
  )
  const isOwnProfile = useMemo(() => {
    const urlUserName = (query.username as string)?.toLocaleLowerCase()
    if (!urlUserName) return false
    return urlUserName === user?.usernameLower
  }, [user, query])

  const urlUsername = query.username as string
  const setProfile = useCallback((profile: IUser) => {
    dispatch({ type: ProfilePageActionTypes.SET_PROFILE, payload: profile })
    dispatch({ type: ProfilePageActionTypes.SET_EDIT, payload: false })
  }, [])

  React.useEffect(() => {
    // listen to route change
    const handleRouteChange = async (url: string) => {
      if (url === currentPath) return
      if (currentPath !== '' && state?.profile?.id) {
        await mixPanelTrack(url, state.profile?.id)
      }
      setCurrentPath(url)
    }

    if (currentPath === '') {
      handleRouteChange(window.location.pathname)
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events, currentPath])

  React.useEffect(() => {
    if (urlUsername === user?.usernameLower) {
      setProfile(user)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, urlUsername])

  React.useEffect(() => {
    if (!isOwnProfile && state.profile && user.id) {
      const { sendMessage, ...rest } = query
      if (sendMessage) {
        showToast([state.profile])
        const newParams = rest
        router.replace({ pathname, query: newParams }, undefined, {
          shallow: true,
        })
      }
    }
  }, [state.profile, isOwnProfile, query, showToast, user, router, pathname])

  return (
    <ProfilePageContext.Provider
      value={{
        ...state,
        ownProfile: isOwnProfile,
        setProfile,
      }}
    >
      {children}
    </ProfilePageContext.Provider>
  )
}

export const getProfileServerSideProps: GetServerSideProps = async (
  context
) => {
  const { req } = context
  let accessToken = null
  if (req.cookies.token) {
    accessToken = req.cookies.token
  }

  const res = context.res
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=30, stale-while-revalidate=120'
  )
  const username = context.query.username as string
  const currentUrl = req.url

  if (!username) {
    return { notFound: true }
  }

  const result: IUser = await getUserByUsernameWithTokenAndUrl({
    username,
    url: req?.cookies?.pathname || currentUrl || '',
    token: accessToken,
  })

  if (result?.id) {
    const metaProps = {
      title: getUserShareTitle(result),
      description: `View ${result.firstName} ${result.lastName}’s projects & analytics on Creatorland.com. Creatorland is a professional network for the creator economy, helping creators like ${result.firstName} ${result.lastName} build relationships with talent managers, marketers, and professionals.`,
      image: result?.avatar || '',
      twitterImage: result?.avatar || '',
    }
    return { props: { profile: result, metaProps } }
  } else {
    return { notFound: true }
  }
}
